import React, { useEffect, useState } from 'react'

import { ReactComponent as CsnLogo } from 'assets/images/logo_csn.svg'
import Logotipo from 'assets/images/logotipo_csn.png'
import { Card, Col, Image, Row, SimpleCollapse, Span } from 'components'
import { ExcelIcon, PdfIcon } from 'components/Svg'
import { useAuth, useLoading, useModal, useReportPrint } from 'hooks'
import {
  IHandlePrintConsolidacaoEfetivo,
  IRelatorioQuantidadeEfetivoContent
} from 'hooks/useReportPrint/types'
import { PERMITIONS } from 'permitions'
import {
  ReportsConsolidationServices,
  IConsolidacaoTableEntity
} from 'services/NGT/EffectiveConsolidation'
import { COLORS, FONTS, FORMAT, SPACING, TYPOGRAPHY } from 'utils'

import { downloadURI } from '../../EffectiveConsolidation'
import { ReportTable } from './components/ReportTable/ReportTable'
import { FormatEffectiveQuantityNumbers as FormatNumber} from './utils/FormatEffectiveQuantityNumbers'

interface ICollapsesPropsPDF {
  collapseTitle: string
  tableTitle: string
}

interface IReportEffetiveQuantity {
  flRecorrente: boolean
}

const ReportEffetiveQuantity: React.FC<IReportEffetiveQuantity> = ({ flRecorrente }) => {
  const { setLoading, setMsgErro } = useLoading()
  const { showModal } = useModal()
  const { user } = useAuth()
  const { handlePrintQuantidadeEfetivo } = useReportPrint()
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear())

  const [isGeneratePDF, setIsGeneratePDF] = useState<boolean>(false)
  const [reportEffetiveQuantityByArea, setReportEffetiveQuantityByArea] =
    useState<IConsolidacaoTableEntity>()
  const [reportEffetiveQuantityByCompany, setReportEffetiveQuantityByCompany] =
    useState<IConsolidacaoTableEntity>()
  const [
    reportEffetiveQuantityByExecutive,
    setReportEffetiveQuantityByExecutive
  ] = useState<IConsolidacaoTableEntity>()
  const [reportEffetiveQuantityByUnit, setReportEffetiveQuantityByUnit] =
    useState<IConsolidacaoTableEntity>()

  const collapsesTitles: ICollapsesPropsPDF[] = [
    {
      collapseTitle: 'Distribuição por Diretoria Executiva',
      tableTitle: 'Diretoria Executiva'
    },
    {
      collapseTitle: 'Distribuição por Empresas',
      tableTitle: 'Empresa'
    },
    {
      collapseTitle: 'Distribuição por Área',
      tableTitle: 'Área'
    },
    {
      collapseTitle: 'Distribuição por Unidade de Negócio',
      tableTitle: 'Unidade Negócio'
    }
  ]

  const generateExcelEffetiveQuantity = async () => {
    setLoading('loading')

    ReportsConsolidationServices()
      .GetExcelQuantidadeEfetivo(
        user.IdUsuario,
        user.PerfilUsuarioId === PERMITIONS.CSNCONTRAPARTE,
        flRecorrente
      )
      .then((response) => {
        if (response.status === 200) {
          downloadURI(response.data, `quantidade-efetivo.xlsx`)
        }
        setLoading('idle')
      })
      .catch((error) => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            error.mensagem ||
            error.message ||
            'Não foi possivel fazer o download da planilha, tente novamente!',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }

  const handleClickPdfReport = async () => {
    setLoading('loading')

    try {
      setLoading('idle')

      const pdfDataArray: IRelatorioQuantidadeEfetivoContent[] = []

      collapsesTitles.forEach((collapseTitle) => {
        formatNumerosDoPDF(reportEffetiveQuantityByExecutive, reportEffetiveQuantityByCompany, reportEffetiveQuantityByArea, reportEffetiveQuantityByUnit)

        pdfDataArray.push({
          anoAnterior: currentYear - 1,
          anoAtual: currentYear,
          body:
            collapseTitle.collapseTitle ===
              'Distribuição por Diretoria Executiva'
              ? reportEffetiveQuantityByExecutive.items.map((item, index) => {
                const effectiveByExecutiveArray: (string | number)[][] = [] // Array auxiliar
                // Criando as listas principais
                effectiveByExecutiveArray.push([
                  item?.nmGrupoConsolidacao.toUpperCase(),
                  item?.informaoAnoMeses[12]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[0]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[1]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[2]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[3]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[4]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[5]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[6]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[7]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[8]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[9]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[10]?.info?.qtdTotalEfetivo,
                  item?.informaoAnoMeses[11]?.info?.qtdTotalEfetivo,
                  FormatNumber.formatarNumero(item?.variacao) + "%"
                ])
                // Adicionando as listas de subItens
                item.unidadeGestoraInfo.forEach((subItem) =>
                  effectiveByExecutiveArray.push([
                    FORMAT.capitalizeString(
                      subItem?.nmUnidadeGestora.toLowerCase()
                    ),
                    subItem?.informaoAnoMeses[12]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[0]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[1]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[2]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[3]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[4]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[5]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[6]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[7]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[8]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[9]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[10]?.info?.qtdTotalEfetivo,
                    subItem?.informaoAnoMeses[11]?.info?.qtdTotalEfetivo,
                    FormatNumber.formatarNumero(subItem?.variacao) + "%"
                  ])
                )
                !reportEffetiveQuantityByExecutive.items[index + 1] && effectiveByExecutiveArray.push([
                  "Total",
                  reportEffetiveQuantityByExecutive?.total[12]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[0]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[1]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[2]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[3]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[4]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[5]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[6]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[7]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[8]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[9]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[10]?.info?.qtdTotalEfetivo,
                  reportEffetiveQuantityByExecutive?.total[11]?.info?.qtdTotalEfetivo,
                ])

                return effectiveByExecutiveArray
              })
              : collapseTitle.collapseTitle === 'Distribuição por Empresas'
                ? reportEffetiveQuantityByCompany.items.map((item, index) => {
                  const effectiveByCompanyArray: (string | number)[][] = [] // Array auxiliar
                  // Criando as listas principais
                  effectiveByCompanyArray.push([
                    item?.nmGrupoConsolidacao.toUpperCase(),
                    item?.informaoAnoMeses[12]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[0]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[1]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[2]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[3]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[4]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[5]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[6]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[7]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[8]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[9]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[10]?.info?.qtdTotalEfetivo,
                    item?.informaoAnoMeses[11]?.info?.qtdTotalEfetivo,
                    FormatNumber.formatarNumero(item?.variacao) + "%"
                  ])
                  // Adicionando as listas de subItens
                  item.unidadeGestoraInfo.forEach((subItem) =>
                    effectiveByCompanyArray.push([
                      FORMAT.capitalizeString(
                        subItem?.nmUnidadeGestora.toLowerCase()
                      ),
                      subItem?.informaoAnoMeses[12]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[0]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[1]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[2]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[3]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[4]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[5]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[6]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[7]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[8]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[9]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[10]?.info?.qtdTotalEfetivo,
                      subItem?.informaoAnoMeses[11]?.info?.qtdTotalEfetivo,
                      FormatNumber.formatarNumero(subItem?.variacao) + "%"
                    ])
                  )
                  !reportEffetiveQuantityByCompany.items[index + 1] && effectiveByCompanyArray.push([
                    "Total",
                    reportEffetiveQuantityByCompany?.total[12]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[0]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[1]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[2]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[3]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[4]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[5]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[6]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[7]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[8]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[9]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[10]?.info?.qtdTotalEfetivo,
                    reportEffetiveQuantityByCompany?.total[11]?.info?.qtdTotalEfetivo,
                  ])

                  return effectiveByCompanyArray
                })
                : collapseTitle.collapseTitle === 'Distribuição por Área'
                  ? reportEffetiveQuantityByArea.items.map((item, index) => {
                    const effectiveByAreaArray: (string | number)[][] = [] // Array auxiliar
                    // Criando as listas principais
                    effectiveByAreaArray.push([
                      item?.nmGrupoConsolidacao.toUpperCase(),
                      item?.informaoAnoMeses[12]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[0]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[1]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[2]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[3]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[4]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[5]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[6]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[7]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[8]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[9]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[10]?.info?.qtdTotalEfetivo,
                      item?.informaoAnoMeses[11]?.info?.qtdTotalEfetivo,
                      FormatNumber.formatarNumero(item?.variacao) + "%"
                    ])
                    // Adicionando as listas de subItens
                    item.unidadeGestoraInfo.forEach((subItem) =>
                      effectiveByAreaArray.push([
                        FORMAT.capitalizeString(
                          subItem?.nmUnidadeGestora.toLowerCase()
                        ),
                        subItem?.informaoAnoMeses[12]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[0]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[1]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[2]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[3]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[4]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[5]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[6]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[7]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[8]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[9]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[10]?.info?.qtdTotalEfetivo,
                        subItem?.informaoAnoMeses[11]?.info?.qtdTotalEfetivo,
                        FormatNumber.formatarNumero(subItem?.variacao) + "%"
                      ])
                    )

                    !reportEffetiveQuantityByArea.items[index + 1] && effectiveByAreaArray.push([
                      "Total",
                      reportEffetiveQuantityByArea?.total[12]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[0]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[1]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[2]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[3]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[4]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[5]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[6]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[7]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[8]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[9]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[10]?.info?.qtdTotalEfetivo,
                      reportEffetiveQuantityByArea?.total[11]?.info?.qtdTotalEfetivo,
                    ])

                    return effectiveByAreaArray
                  })
                  : collapseTitle.collapseTitle ===
                    'Distribuição por Unidade de Negócio'
                    ? reportEffetiveQuantityByUnit.items.map((item, index) => {
                      const effectiveByUnityArray: (string | number)[][] = [] // Array auxiliar
                      // Criando as listas principais
                      effectiveByUnityArray.push([
                        item?.nmGrupoConsolidacao.toUpperCase(),
                        item?.informaoAnoMeses[12]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[0]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[1]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[2]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[3]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[4]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[5]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[6]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[7]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[8]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[9]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[10]?.info?.qtdTotalEfetivo,
                        item?.informaoAnoMeses[11]?.info?.qtdTotalEfetivo,
                        FormatNumber.formatarNumero(item?.variacao) + "%"
                      ])
                      // Adicionando as listas de subItens
                      item.unidadeGestoraInfo.forEach((subItem) =>
                        effectiveByUnityArray.push([
                          FORMAT.capitalizeString(
                            subItem?.nmUnidadeGestora.toLowerCase()
                          ),
                          subItem?.informaoAnoMeses[12]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[0]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[1]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[2]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[3]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[4]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[5]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[6]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[7]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[8]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[9]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[10]?.info?.qtdTotalEfetivo,
                          subItem?.informaoAnoMeses[11]?.info?.qtdTotalEfetivo,
                          FormatNumber.formatarNumero(subItem?.variacao) + "%"
                        ])
                      )

                      !reportEffetiveQuantityByUnit.items[index + 1] && effectiveByUnityArray.push([
                        "Total",
                        reportEffetiveQuantityByUnit?.total[12]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[0]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[1]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[2]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[3]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[4]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[5]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[6]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[7]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[8]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[9]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[10]?.info?.qtdTotalEfetivo,
                        reportEffetiveQuantityByUnit?.total[11]?.info?.qtdTotalEfetivo,
                      ])


                      return effectiveByUnityArray
                    })
                    : [],
          tableTitle: collapseTitle.tableTitle,
          titleCollapse: collapseTitle.collapseTitle
        })
      })

      if (pdfDataArray) {
        const pdfObject: IHandlePrintConsolidacaoEfetivo = {
          data: pdfDataArray,
          filename: `relatorio-consolidacao-quantidade-efetivo`
        }
        handlePrintQuantidadeEfetivo(pdfObject)
      } else {
        showModal({
          hideButtonCancel: true,
          message: 'Não há efetivo cadastrado para este contrato.',
          txtButtonConfirm: 'Fechar',
          type: 'alert'
        })
      }
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Não foi possivel obter a lista de Efetivos deste contrato',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const HeaderEffectiveConsolidationTitle = () => (
    <Card
      backgroundColor={COLORS.headerColor}
      padding={SPACING.spacing20}
      marginBottom={`-${SPACING.spacing15}`}
    >
      <Row alignItems="center" justifyContent="left">
        <Col gap=".7rem" alignItems="center" justifyContent="left">
          <CsnLogo width="62" height="40" />
          <Image src={Logotipo} width="3.8rem" />
        </Col>
        <Col justifyContent="center" marginLeft={SPACING.spacing200}>
          <Span
            text="RELATÓRIO DE QUANTIDADE EFETIVO"
            color={COLORS.blueFonts}
            fontSize="24px"
            fontWeight={FONTS.bold}
            textTransform="uppercase"
            style={{
              display: 'flex',
              justifyContent: 'center'
            }}
          />
          <Span
            textAlign="center"
            fontWeight="bold"
            fontSize={TYPOGRAPHY.bodySize16}
            color={COLORS.blueFonts}
            text={`${flRecorrente ? "APENAS RECORRENTES - " : ""} Distribuição por DIR EXEC / EMPRESAS / ÁREA / UN NEGÓCIO - do Grupo CSN`}
            marginTop={SPACING.spacing15}
            marginBottom={SPACING.spacing15}
          />
        </Col>
      </Row>
    </Card>
  )

  const getReportEffetiveQuantity = async () => {
    setLoading('loading')

    try {
      const response =
        await ReportsConsolidationServices().GetQuantidadeEfetivo(
          user.IdUsuario,
          user.PerfilUsuarioId === PERMITIONS.CSNCONTRAPARTE,
          flRecorrente
        )

      if (response.status !== 200) throw new Error(response.statusText)

      setCurrentYear(response.data.entity.area?.total?.find((info) => info.mes === 1).ano)
      setReportEffetiveQuantityByArea(response.data.entity.area)
      setReportEffetiveQuantityByCompany(response.data.entity.empresa)
      setReportEffetiveQuantityByExecutive(response.data.entity.diretoria)
      setReportEffetiveQuantityByUnit(response.data.entity.unidadeNegocio)

      setLoading('idle')
    } catch (error: any) {
      setMsgErro('Erro ao carregar dados do relatório, tente novamente!')
      setLoading('error')
    }
  }

  useEffect(() => {
    getReportEffetiveQuantity()
  }, [])

  return (
    <Card
      marginLeft={SPACING.spacing20}
      marginRight={SPACING.spacing20}
      padding={SPACING.spacing20}
      marginBottom={SPACING.spacing100}
      boxShadow={`0 0 1em ${COLORS.lightGray}`}
    >
      <div id="pdfView">
        {isGeneratePDF ? (
          <HeaderEffectiveConsolidationTitle />
        ) : (
          <>
            <Row
              justifyContent="center"
              marginTop={SPACING.spacing15}
              marginBottom={SPACING.spacing5}
            >
              <Span
                textAlign="center"
                fontWeight="bold"
                fontSize={TYPOGRAPHY.bodySize16}
                color={COLORS.blueFonts}
                text={`${flRecorrente ? "APENAS RECORRENTES - " : ""} Distribuição por DIR EXEC / EMPRESAS / ÁREA / UN NEGÓCIO - do Grupo CSN`}
                marginTop={SPACING.spacing15}
                marginBottom={SPACING.spacing5}
              />
            </Row>
            <Row justifyContent="flex-end">
              <ExcelIcon onClick={() => generateExcelEffetiveQuantity()} />
              <PdfIcon onClick={() => handleClickPdfReport()} />
            </Row>
          </>
        )}
        <Row marginBottom={SPACING.spacing30} />
        <SimpleCollapse
          title="Distribuição por Diretoria Executiva"
          isGeneratePDF={isGeneratePDF}
        >
          <ReportTable
            currentYear={currentYear}
            tableData={reportEffetiveQuantityByExecutive}
            titleHeader="Diretoria Executiva"
            isGeneratePDF={isGeneratePDF}
          />
        </SimpleCollapse>

        <SimpleCollapse
          title="Distribuição por Empresas"
          isGeneratePDF={isGeneratePDF}
        >
          <ReportTable
            currentYear={currentYear}
            tableData={reportEffetiveQuantityByCompany}
            titleHeader="Empresa"
            isGeneratePDF={isGeneratePDF}
          />
        </SimpleCollapse>

        <SimpleCollapse
          title="Distribuição por Área"
          isGeneratePDF={isGeneratePDF}
        >
          <ReportTable
            currentYear={currentYear}
            tableData={reportEffetiveQuantityByArea}
            titleHeader="Área"
            isGeneratePDF={isGeneratePDF}
          />
        </SimpleCollapse>

        <SimpleCollapse
          title="Distribuição por Unidade de Negócio"
          isGeneratePDF={isGeneratePDF}
        >
          <ReportTable
            currentYear={currentYear}
            tableData={reportEffetiveQuantityByUnit}
            titleHeader="Unidade Negócio"
            isGeneratePDF={isGeneratePDF}
          />
        </SimpleCollapse>
      </div>
    </Card>
  )
}

function formatNumerosDoPDF(
  reportEffetiveQuantityByExecutive: IConsolidacaoTableEntity,
  reportEffetiveQuantityByCompany: IConsolidacaoTableEntity,
  reportEffetiveQuantityByArea: IConsolidacaoTableEntity,
  reportEffetiveQuantityByUnit: IConsolidacaoTableEntity
  ): void{
  FormatNumber.formatarTableEntity(reportEffetiveQuantityByExecutive)
  FormatNumber.formatarTableEntity(reportEffetiveQuantityByCompany)
  FormatNumber.formatarTableEntity(reportEffetiveQuantityByArea)
  FormatNumber.formatarTableEntity(reportEffetiveQuantityByUnit)

  FormatNumber.formatarSubItensTableData(reportEffetiveQuantityByExecutive)
  FormatNumber.formatarSubItensTableData(reportEffetiveQuantityByCompany)
  FormatNumber.formatarSubItensTableData(reportEffetiveQuantityByArea)
  FormatNumber.formatarSubItensTableData(reportEffetiveQuantityByUnit)
}

export default ReportEffetiveQuantity
